import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Collapse,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Article as ArticleIcon,
  ViewModule as ContentIcon,
  Category as CategoryIcon,
  LocalOffer as OfferIcon,
  Group as GroupIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  Visibility as VisibilityIcon,
  AttachMoney as AttachMoneyIcon,
  ExpandMore,
  ExpandLess,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

// Modern Table Styles based on UI Foundations Kit Simple Table
const tableStyles = {
  container: {
    borderRadius: 2,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    border: 'none',
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#FAFAFA',
  },
  headerCell: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 2,
  },
  sortableHeaderCell: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 2,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  bodyRow: {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  bodyRowAlternate: {
    backgroundColor: '#FFFFFF',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  bodyCell: {
    borderBottom: '1px solid #F3F4F6',
    py: 1.5,
    px: 2,
    fontSize: '0.875rem',
    color: '#111827',
  },
  groupRow: {
    backgroundColor: '#F3F4F6',
    borderBottom: '1px solid #E5E7EB',
  },
  emptyRow: {
    py: 6,
    textAlign: 'center',
  },
};

// Nested table styles for page breakdowns
const nestedTableStyles = {
  container: {
    borderRadius: 1,
    backgroundColor: '#FAFAFA',
    border: '1px solid #E5E7EB',
    mt: 1,
    mb: 2,
  },
  headerCell: {
    fontWeight: 600,
    color: '#6B7280',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1,
    px: 1.5,
    backgroundColor: '#F9FAFB',
  },
  bodyCell: {
    borderBottom: '1px solid #F3F4F6',
    py: 1,
    px: 1.5,
    fontSize: '0.8rem',
    color: '#374151',
  },
  snakeRow: {
    backgroundColor: '#FEF3C7',
  },
};

// Mock Chart Component
const MockChart = ({ title, type = 'line', height = 300 }) => {
  const generateRandomData = () => {
    return Array.from({ length: 12 }, () => Math.floor(Math.random() * 100));
  };

  const data = generateRandomData();
  const maxValue = Math.max(...data);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ height: height, display: 'flex', alignItems: 'end', gap: 1, p: 2 }}>
          {type === 'bar' ? (
            data.map((value, index) => (
              <Box
                key={index}
                sx={{
                  height: `${(value / maxValue) * 100}%`,
                  width: '100%',
                  backgroundColor: 'primary.main',
                  opacity: 0.7,
                  borderRadius: '4px 4px 0 0',
                  minHeight: '4px'
                }}
              />
            ))
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 300 200">
              <polyline
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                points={data.map((value, index) =>
                  `${(index / (data.length - 1)) * 300},${200 - (value / maxValue) * 180}`
                ).join(' ')}
              />
              {data.map((value, index) => (
                <circle
                  key={index}
                  cx={(index / (data.length - 1)) * 300}
                  cy={200 - (value / maxValue) * 180}
                  r="4"
                  fill="#3B82F6"
                />
              ))}
            </svg>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Mock Pie Chart Component
const MockPieChart = ({ title, data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ position: 'relative', width: 200, height: 200 }}>
            <svg width="200" height="200" viewBox="0 0 200 200">
              {data.map((item, index) => {
                const percentage = (item.value / total) * 100;
                const angle = (item.value / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                currentAngle += angle;

                const x1 = 100 + 80 * Math.cos((startAngle - 90) * Math.PI / 180);
                const y1 = 100 + 80 * Math.sin((startAngle - 90) * Math.PI / 180);
                const x2 = 100 + 80 * Math.cos((endAngle - 90) * Math.PI / 180);
                const y2 = 100 + 80 * Math.sin((endAngle - 90) * Math.PI / 180);
                const largeArc = angle > 180 ? 1 : 0;

                return (
                  <path
                    key={index}
                    d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={item.color}
                    opacity="0.8"
                  />
                );
              })}
            </svg>
          </Box>
          <Box>
            {data.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: item.color,
                    borderRadius: '2px'
                  }}
                />
                <Typography variant="body2">
                  {item.label}: {((item.value / total) * 100).toFixed(1)}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Reporting = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Mock data for other tabs (not Offer Stats)


  const affiliateStatsData = {
    totalAffiliates: 89,
    activeAffiliates: 67,
    topPerformers: [
      { name: 'john@email.com', commissions: '$2,450', conversions: 45 },
      { name: 'sarah@email.com', commissions: '$1,890', conversions: 38 },
      { name: 'mike@email.com', commissions: '$1,650', conversions: 32 },
      { name: 'lisa@email.com', commissions: '$1,320', conversions: 28 },
      { name: 'alex@email.com', commissions: '$980', conversions: 22 }
    ]
  };

  // Blog Stats - Real state
  const [blogStats, setBlogStats] = useState(null);
  const [blogStatsLoading, setBlogStatsLoading] = useState(false);
  const [blogStatsError, setBlogStatsError] = useState('');
  const [expandedBlogs, setExpandedBlogs] = useState({});
  const [expandedAffiliates, setExpandedAffiliates] = useState({});
  const [blogDateRange, setBlogDateRange] = useState('30');
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogSelectedCategory, setBlogSelectedCategory] = useState('');
  const [blogSelectedSubcategory, setBlogSelectedSubcategory] = useState('');
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [viewAllAffiliates, setViewAllAffiliates] = useState(true);
  const [expandedSnakeBlogs, setExpandedSnakeBlogs] = useState({});

  const dateRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' }
  ];

  const pieChartData = [
    { label: 'Active', value: 65, color: '#4caf50' },
    { label: 'Expired', value: 20, color: '#f44336' },
    { label: 'Draft', value: 10, color: '#ff9800' },
    { label: 'Pending', value: 5, color: '#9e9e9e' }
  ];

  const categoryData = [
    { label: 'Technology', value: 35, color: '#3B82F6' },
    { label: 'Fashion', value: 25, color: '#eabbc3' },
    { label: 'Health', value: 20, color: '#4caf50' },
    { label: 'Travel', value: 12, color: '#ff9800' },
    { label: 'Food', value: 8, color: '#9c27b0' }
  ];

  // State for Offer Stats
  const [offerStatsData, setOfferStatsData] = useState([]);
  const [offerStatsLoading, setOfferStatsLoading] = useState(false);
  const [offerStatsSummary, setOfferStatsSummary] = useState({
    totalClicks: 0,
    totalConversions: 0,
    totalRevenue: 0,
    averageCPA: 0
  });
  const [filterOptions, setFilterOptions] = useState({
    networks: [],
    brands: [],
    offers: []
  });

  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 6 months ago
    endDate: new Date().toISOString().split('T')[0],
    networkCompany: '',
    brandName: '',
    offerId: ''
  });

  // Sorting states
  const [sortColumn, setSortColumn] = useState('clicks');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  // Fetch offer stats data
  const fetchOfferStats = async (customFilters = null) => {
    setOfferStatsLoading(true);
    try {
      // Use custom filters if provided, otherwise use state filters
      const activeFilters = customFilters || filters;

      const params = new URLSearchParams({
        start_date: activeFilters.startDate,
        end_date: activeFilters.endDate
      });

      if (activeFilters.networkCompany) params.append('network_company', activeFilters.networkCompany);
      if (activeFilters.brandName) params.append('brand_name', activeFilters.brandName);
      if (activeFilters.offerId) params.append('offer_id', activeFilters.offerId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/affiliate/admin/offer-stats?${params}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        console.log('✅ Offer stats received:', response.data);
        setOfferStatsData(response.data.stats);
        setOfferStatsSummary(response.data.summary);
        setFilterOptions(response.data.filters);
      } else {
        console.error('❌ API returned success:false', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching offer stats:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
    } finally {
      setOfferStatsLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    if (activeTab === 0) {
      fetchOfferStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load categories for Blog Stats
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get('/categories/select');
        setBlogCategories(response.data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load affiliates for admin
  useEffect(() => {
    const loadAffiliates = async () => {
      try {
        const response = await api.get('/affiliates');
        setAffiliates(response.data || []);
      } catch (error) {
        console.error('Error loading affiliates:', error);
      }
    };
    loadAffiliates();
  }, []);

  // Fetch Blog Stats
  useEffect(() => {
    const fetchBlogStats = async () => {
      if (activeTab !== 2) return; // Only fetch when Blog Stats tab is active

      setBlogStatsLoading(true);
      setBlogStatsError('');
      try {
        const targetAffiliateId = !viewAllAffiliates && selectedAffiliateId
          ? selectedAffiliateId
          : 'all';

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(blogDateRange));

        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });

        if (blogSelectedCategory) {
          params.append('categoryId', blogSelectedCategory);
        }
        if (blogSelectedSubcategory) {
          params.append('subcategoryId', blogSelectedSubcategory);
        }
        if (viewAllAffiliates) {
          params.append('viewAll', 'true');
        }

        console.log('Fetching blog performance stats for affiliate:', targetAffiliateId);

        const response = await api.get(`/affiliate/blog-performance/${targetAffiliateId}?${params}`);

        console.log('Blog performance stats response:', response.data);
        setBlogStats(response.data?.stats || null);
      } catch (error) {
        console.error('Error fetching blog stats:', error);
        setBlogStatsError('Failed to load blog performance data');
        setBlogStats(null);
      } finally {
        setBlogStatsLoading(false);
      }
    };

    fetchBlogStats();
  }, [activeTab, blogDateRange, blogSelectedCategory, blogSelectedSubcategory, selectedAffiliateId, viewAllAffiliates]);

  // Toggle blog expansion
  const handleToggleBlog = (blogPostId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogPostId]: !prev[blogPostId]
    }));
  };

  // Toggle affiliate expansion
  const handleToggleAffiliate = (affiliateId) => {
    setExpandedAffiliates(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchOfferStats();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      networkCompany: '',
      brandName: '',
      offerId: ''
    };
    setFilters(clearedFilters);
    // Immediately fetch with cleared filters
    fetchOfferStats(clearedFilters);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        // If already desc, clear sorting
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      // If clicking a new column, set it to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort the offer stats data
  const getSortedData = () => {
    if (!sortColumn) return offerStatsData;

    const sorted = [...offerStatsData].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'networkCompany':
          aValue = (a.networkCompany || '').toLowerCase();
          bValue = (b.networkCompany || '').toLowerCase();
          break;
        case 'brandName':
          aValue = (a.brandName || '').toLowerCase();
          bValue = (b.brandName || '').toLowerCase();
          break;
        case 'offerTitle':
          aValue = (a.offerTitle || '').toLowerCase();
          bValue = (b.offerTitle || '').toLowerCase();
          break;
        case 'clicks':
          aValue = a.clicks || 0;
          bValue = b.clicks || 0;
          break;
        case 'conversions':
          aValue = a.conversions || 0;
          bValue = b.conversions || 0;
          break;
        case 'cpa':
          aValue = a.cpa || 0;
          bValue = b.cpa || 0;
          break;
        case 'totalPayout':
          aValue = a.totalPayout || 0;
          bValue = b.totalPayout || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    return sorted;
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.3 }} />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  const renderOfferStats = () => (
    <Box>
      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Network / Company</InputLabel>
                <Select
                  value={filters.networkCompany}
                  onChange={(e) => handleFilterChange('networkCompany', e.target.value)}
                  label="Network / Company"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.networks.map((network) => (
                    <MenuItem key={network} value={network}>
                      {network}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand Name</InputLabel>
                <Select
                  value={filters.brandName}
                  onChange={(e) => handleFilterChange('brandName', e.target.value)}
                  label="Brand Name"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Offer</InputLabel>
                <Select
                  value={filters.offerId}
                  onChange={(e) => handleFilterChange('offerId', e.target.value)}
                  label="Offer"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.offers.map((offer) => (
                    <MenuItem key={offer.id} value={offer.id}>
                      {offer.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  disabled={offerStatsLoading}
                  sx={{ minWidth: 120 }}
                >
                  {offerStatsLoading ? <CircularProgress size={24} /> : 'Apply Filters'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={offerStatsLoading}
                  sx={{ minWidth: 120 }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="primary" />
                <Box>
                  <Typography variant="h4">{offerStatsSummary.totalClicks.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Clicks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="success" />
                <Box>
                  <Typography variant="h4">{offerStatsSummary.totalConversions.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Sales</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <OfferIcon color="info" />
                <Box>
                  <Typography variant="h4">${(offerStatsSummary.averageCPA || 0).toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">Average CPA</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">${(offerStatsSummary.totalRevenue || 0).toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Offer Performance Report
          </Typography>
          {offerStatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={tableStyles.headerRow}>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('networkCompany')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Network / Company
                        {renderSortIcon('networkCompany')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('brandName')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Brand Name
                        {renderSortIcon('brandName')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('offerTitle')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Offer
                        {renderSortIcon('offerTitle')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('clicks')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Clicks
                        {renderSortIcon('clicks')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('conversions')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Sales
                        {renderSortIcon('conversions')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('cpa')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        CPA
                        {renderSortIcon('cpa')}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={tableStyles.sortableHeaderCell}
                      onClick={() => handleSort('totalPayout')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Revenue
                        {renderSortIcon('totalPayout')}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offerStatsData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={tableStyles.emptyRow}>
                        <Typography variant="body1" color="text.secondary">
                          No data available for the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getSortedData().map((row, index) => (
                      <TableRow
                        key={index}
                        sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                      >
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.networkCompany || 'N/A'}
                        </TableCell>
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.brandName || 'N/A'}
                        </TableCell>
                        <TableCell sx={tableStyles.bodyCell}>
                          {row.offerTitle || 'N/A'}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          {row.clicks.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          {row.conversions.toLocaleString()}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          ${row.cpa.toFixed(2)}
                        </TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          ${row.totalPayout.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderAffiliateStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="primary" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.totalAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">{affiliateStatsData.activeAffiliates}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Affiliates</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">$12,450</Typography>
                  <Typography variant="body2" color="text.secondary">Total Commissions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">238</Typography>
                  <Typography variant="body2" color="text.secondary">Total Conversions</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MockChart title="Affiliate Signups Over Time" type="bar" />
        </Grid>
        <Grid item xs={12} md={6}>
          <MockChart title="Commission Trends" type="line" />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Performing Affiliates</Typography>
              <TableContainer component={Paper} sx={tableStyles.container}>
                <Table>
                  <TableHead>
                    <TableRow sx={tableStyles.headerRow}>
                      <TableCell sx={tableStyles.headerCell}>Affiliate</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Commissions</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Conversions</TableCell>
                      <TableCell align="center" sx={tableStyles.headerCell}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {affiliateStatsData.topPerformers.map((affiliate, index) => (
                      <TableRow
                        key={index}
                        sx={index % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}
                      >
                        <TableCell sx={tableStyles.bodyCell}>{affiliate.name}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>{affiliate.commissions}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>{affiliate.conversions}</TableCell>
                        <TableCell align="center" sx={tableStyles.bodyCell}>
                          <Chip label="Active" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderBlogStats = () => (
    <Box>
      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>View Mode</InputLabel>
                <Select
                  value={viewAllAffiliates ? 'all' : 'single'}
                  label="View Mode"
                  onChange={(e) => {
                    const viewAll = e.target.value === 'all';
                    setViewAllAffiliates(viewAll);
                    if (!viewAll) {
                      setSelectedAffiliateId('');
                    }
                  }}
                  size="small"
                >
                  <MenuItem value="all">All Affiliates</MenuItem>
                  <MenuItem value="single">Single Affiliate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!viewAllAffiliates && (
              <Grid item>
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel>Affiliate</InputLabel>
                  <Select
                    value={selectedAffiliateId}
                    label="Affiliate"
                    onChange={(e) => setSelectedAffiliateId(e.target.value)}
                    size="small"
                  >
                    {affiliates.map((affiliate) => (
                      <MenuItem key={affiliate._id} value={affiliate._id}>
                        {affiliate.name || affiliate.email} {affiliate.affiliateData?.name ? `(${affiliate.affiliateData.name})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={blogDateRange}
                  label="Date Range"
                  onChange={(e) => setBlogDateRange(e.target.value)}
                  size="small"
                >
                  {dateRanges.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={blogSelectedCategory}
                  label="Category"
                  onChange={(e) => {
                    setBlogSelectedCategory(e.target.value);
                    setBlogSelectedSubcategory('');
                  }}
                  size="small"
                >
                  <MenuItem value="">
                    <em>All Categories</em>
                  </MenuItem>
                  {blogCategories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ minWidth: 200 }} disabled={!blogSelectedCategory}>
                <InputLabel>Subcategory</InputLabel>
                <Select
                  value={blogSelectedSubcategory}
                  label="Subcategory"
                  onChange={(e) => setBlogSelectedSubcategory(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">
                    <em>All Subcategories</em>
                  </MenuItem>
                  {blogSelectedCategory && blogCategories.find(c => c._id === blogSelectedCategory)?.subcategories?.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {sub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {(blogSelectedCategory || blogSelectedSubcategory) && (
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setBlogSelectedCategory('');
                    setBlogSelectedSubcategory('');
                  }}
                  size="small"
                >
                  Clear Filters
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {blogStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : blogStatsError ? (
        <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
      ) : blogStats ? (
        <>
          {/* Overall Blog Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VisibilityIcon color="primary" />
                    <Box>
                      <Typography variant="h4">{(blogStats.overall?.totalViews || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Blog Views</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon color="success" />
                    <Box>
                      <Typography variant="h4">{(blogStats.overall?.sales || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Sales</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachMoneyIcon color="warning" />
                    <Box>
                      <Typography variant="h4">${(blogStats.overall?.revenue || 0).toFixed(2)}</Typography>
                      <Typography variant="body2" color="text.secondary">Revenue</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssessmentIcon color="info" />
                    <Box>
                      <Typography variant="h4">{(blogStats.overall?.uniqueSessions || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Unique Sessions</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Blog Performance Table */}
          {(() => {
            const hasHeadSnake = blogStats.head && blogStats.snake;
            const blogs = hasHeadSnake ? blogStats.head.blogs : blogStats.byBlog;
            const pageStatsArray = hasHeadSnake ? blogStats.head.pageStats : blogStats.pageStatsByBlog;
            const sectionTotals = hasHeadSnake ? blogStats.head.totals : null;

            if (!blogs || blogs.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  No blog views found for this period. Views will appear here once blog posts are viewed.
                </Typography>
              );
            }

            // Group blogs by affiliate for admin view
            let affiliateGroups = [];
            if (viewAllAffiliates) {
              const groupedByAffiliate = new Map();
              blogs.forEach(blog => {
                const affiliateId = blog.affiliateId || 'unknown';
                if (!groupedByAffiliate.has(affiliateId)) {
                  groupedByAffiliate.set(affiliateId, {
                    affiliateId: blog.affiliateId,
                    affiliateName: blog.affiliateName,
                    affiliateUsername: blog.affiliateUsername,
                    blogs: [],
                    totalViews: 0,
                    totalSales: 0,
                    totalRevenue: 0
                  });
                }
                const group = groupedByAffiliate.get(affiliateId);
                group.blogs.push(blog);
                group.totalViews += blog.totalViews || 0;
                group.totalSales += blog.sales || 0;
                group.totalRevenue += blog.totalRevenue || 0;
              });

              affiliateGroups = Array.from(groupedByAffiliate.values()).sort((a, b) => {
                const nameA = (a.affiliateName || a.affiliateId || '').toLowerCase();
                const nameB = (b.affiliateName || b.affiliateId || '').toLowerCase();
                return nameA.localeCompare(nameB);
              });
            }

            // Create a mapping from affiliate IDs to simple numbers for display
            const affiliateDisplayIdMap = new Map();
            affiliateGroups.forEach((group, index) => {
              affiliateDisplayIdMap.set(group.affiliateId, (index + 1).toString());
            });

            return (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                      Blog Stats
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      {/* Total */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2, py: 0.5, backgroundColor: 'primary.main', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>Total:</Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Views: <strong>{(blogStats.overall?.totalViews || 0).toLocaleString()}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Sales: <strong>{blogStats.overall?.sales || 0}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Revenue: <strong>${(blogStats.overall?.revenue || 0).toFixed(2)}</strong>
                        </Typography>
                      </Box>
                      {/* Head Revenue */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2, py: 0.5, backgroundColor: 'success.main', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>Head:</Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Revenue: <strong>${(blogStats.head?.totals?.revenue || 0).toFixed(2)}</strong>
                        </Typography>
                      </Box>
                      {/* Snake Revenue */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2, py: 0.5, backgroundColor: 'warning.main', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>Snake:</Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Revenue: <strong>${(blogStats.snake?.totals?.revenue || 0).toFixed(2)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} sx={tableStyles.container}>
                    <Table>
                      <TableHead>
                        <TableRow sx={tableStyles.headerRow}>
                          <TableCell padding="checkbox" sx={{ ...tableStyles.headerCell, width: 40 }} />
                          {viewAllAffiliates && <TableCell sx={{ ...tableStyles.headerCell, width: 180 }}>Affiliate</TableCell>}
                          <TableCell sx={{ ...tableStyles.headerCell, width: 200 }}>Blog Title</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Total Views</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Sales</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Revenue</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>RPV</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Pages Tracked</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewAllAffiliates && affiliateGroups.length > 0 ? (
                          affiliateGroups.map((affiliateGroup) => {
                            const affiliateId = affiliateGroup.affiliateId || 'unknown';
                            const isAffiliateExpanded = expandedAffiliates[affiliateId];

                            return (
                              <React.Fragment key={`affiliate-${affiliateId}`}>
                                {/* Affiliate Summary Row */}
                                <TableRow sx={tableStyles.groupRow}>
                                  <TableCell padding="checkbox" sx={tableStyles.bodyCell}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleAffiliate(affiliateId)}
                                    >
                                      {isAffiliateExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell component="th" scope="row" sx={tableStyles.bodyCell}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                        ID: {affiliateDisplayIdMap.get(affiliateId) || affiliateId}
                                      </Typography>
                                      <Typography variant="body2" fontWeight="bold">
                                        {affiliateGroup.affiliateName || affiliateGroup.affiliateId || 'Unknown Affiliate'}
                                      </Typography>
                                      {affiliateGroup.affiliateUsername && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                          @{affiliateGroup.affiliateUsername}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell component="th" scope="row" sx={tableStyles.bodyCell}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {affiliateGroup.blogs.length} Blog{affiliateGroup.blogs.length !== 1 ? 's' : ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    <strong>{affiliateGroup.totalViews.toLocaleString()}</strong>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    <strong>{affiliateGroup.totalSales || 0}</strong>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    <strong>${(affiliateGroup.totalRevenue || 0).toFixed(2)}</strong>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    <strong>
                                      ${affiliateGroup.totalViews > 0
                                        ? ((affiliateGroup.totalRevenue || 0) / affiliateGroup.totalViews).toFixed(2)
                                        : '0.00'}
                                    </strong>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>-</TableCell>
                                </TableRow>

                                {/* Blogs for this affiliate */}
                                {affiliateGroup.blogs.map((blog) => {
                                  const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                                  const pageStats = pageStatsArray?.find(b =>
                                    b.blogPostId?.toString() === blogPostId
                                  );
                                  const isBlogExpanded = expandedBlogs[blogPostId];

                                  return (
                                    <React.Fragment key={`blog-${blogPostId}`}>
                                      <TableRow sx={{ ...tableStyles.bodyRow, display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                        <TableCell padding="checkbox" sx={tableStyles.bodyCell}></TableCell>
                                        <TableCell sx={tableStyles.bodyCell}></TableCell>
                                        <TableCell component="th" scope="row" sx={tableStyles.bodyCell}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                ID: {blogPostId}
                                              </Typography>
                                              <Typography variant="body2">
                                                {blog.blogTitle || blog.blogPostId || 'Unknown Blog'}
                                              </Typography>
                                            </Box>
                                            {pageStats?.pages && pageStats.pages.length > 0 && (
                                              <IconButton
                                                size="small"
                                                onClick={() => handleToggleBlog(blogPostId)}
                                              >
                                                {isBlogExpanded ? <ExpandLess /> : <ExpandMore />}
                                              </IconButton>
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={tableStyles.bodyCell}>{blog.totalViews.toLocaleString()}</TableCell>
                                        <TableCell align="center" sx={tableStyles.bodyCell}>{blog.sales || 0}</TableCell>
                                        <TableCell align="center" sx={tableStyles.bodyCell}>${(blog.totalRevenue || 0).toFixed(2)}</TableCell>
                                        <TableCell align="center" sx={tableStyles.bodyCell}>
                                          ${blog.totalViews > 0
                                            ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                            : '0.00'}
                                        </TableCell>
                                        <TableCell align="center" sx={tableStyles.bodyCell}>
                                          {pageStats?.pages?.length || 0}
                                        </TableCell>
                                      </TableRow>

                                      {/* Page breakdown for this blog */}
                                      {pageStats?.pages && pageStats.pages.length > 0 && (
                                        <TableRow sx={{ display: isAffiliateExpanded ? 'table-row' : 'none' }}>
                                          <TableCell
                                            style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 48 }}
                                            colSpan={8}
                                          >
                                            <Collapse in={isBlogExpanded} timeout="auto" unmountOnExit>
                                              <Box sx={{ margin: 2, marginLeft: '320px' }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                                                  Page Breakdown:
                                                </Typography>
                                                <Table size="small" sx={nestedTableStyles.container}>
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell sx={nestedTableStyles.headerCell}>Page</TableCell>
                                                      <TableCell sx={nestedTableStyles.headerCell}>Page Owner</TableCell>
                                                      <TableCell sx={nestedTableStyles.headerCell}>Offer ID + Name</TableCell>
                                                      <TableCell sx={nestedTableStyles.headerCell}>Offer Element ID + Name</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>Views</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>Clicks</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>CTR</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>Sales</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>Revenue</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>CVR</TableCell>
                                                      <TableCell align="center" sx={nestedTableStyles.headerCell}>RPV</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {pageStats.pages.map((page) => {
                                                      const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                      return (
                                                        <TableRow
                                                          key={isSnake ? 'snake' : page.pageNumber}
                                                          sx={isSnake ? nestedTableStyles.snakeRow : {}}
                                                        >
                                                          <TableCell component="th" scope="row" sx={nestedTableStyles.bodyCell}>
                                                            {isSnake ? 'Snake' : `Page ${page.pageNumber}`}
                                                          </TableCell>
                                                          <TableCell sx={nestedTableStyles.bodyCell}>
                                                            {page.pageOwnerUsername || page.pageOwnerName ? (
                                                              <Box>
                                                                <Typography variant="body2">
                                                                  {page.pageOwnerName || 'N/A'}
                                                                </Typography>
                                                                {page.pageOwnerUsername && (
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                    @{page.pageOwnerUsername}
                                                                  </Typography>
                                                                )}
                                                              </Box>
                                                            ) : (
                                                              <Typography variant="body2" color="text.secondary">-</Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell sx={nestedTableStyles.bodyCell}>
                                                            {page.offerId ? (
                                                              <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                  ID: {page.offerId}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                  {page.offerName || 'Unknown Offer'}
                                                                </Typography>
                                                              </Box>
                                                            ) : (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No offer
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell sx={nestedTableStyles.bodyCell}>
                                                            {page.offerElementId || page.offerElementName ? (
                                                              <Box>
                                                                {page.offerElementId && (
                                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                    ID: {page.offerElementId}
                                                                  </Typography>
                                                                )}
                                                                <Typography variant="body2">
                                                                  {page.offerElementName || 'Unknown Element'}
                                                                </Typography>
                                                              </Box>
                                                            ) : (
                                                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                No element
                                                              </Typography>
                                                            )}
                                                          </TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.views.toLocaleString()}</TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.clicks?.toLocaleString() || 0}</TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.ctr || '0.00'}%</TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.sales || 0}</TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>
                                                            {page.cvr !== undefined ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '0.00%')}
                                                          </TableCell>
                                                          <TableCell align="center" sx={nestedTableStyles.bodyCell}>
                                                            ${page.views > 0
                                                              ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                                              : '0.00'}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          // Render blogs normally (single affiliate view)
                          blogs.map((blog, blogIndex) => {
                            const blogPostId = blog.blogPostId?.toString() || blog.blogPostId;
                            const pageStats = pageStatsArray?.find(b =>
                              b.blogPostId?.toString() === blogPostId
                            );
                            const isExpanded = expandedBlogs[blogPostId];

                            return (
                              <React.Fragment key={blogPostId}>
                                <TableRow sx={blogIndex % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate}>
                                  <TableCell padding="checkbox" sx={tableStyles.bodyCell}></TableCell>
                                  <TableCell component="th" scope="row" sx={tableStyles.bodyCell}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                          ID: {blogPostId}
                                        </Typography>
                                        <Typography variant="body2">
                                          {blog.blogTitle || blog.blogPostId || 'Unknown Blog'}
                                        </Typography>
                                      </Box>
                                      {pageStats?.pages && pageStats.pages.length > 0 && (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleToggleBlog(blogPostId)}
                                        >
                                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>{blog.totalViews.toLocaleString()}</TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>{blog.sales || 0}</TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>${(blog.totalRevenue || 0).toFixed(2)}</TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    ${blog.totalViews > 0
                                      ? ((blog.totalRevenue || 0) / blog.totalViews).toFixed(2)
                                      : '0.00'}
                                  </TableCell>
                                  <TableCell align="center" sx={tableStyles.bodyCell}>
                                    {pageStats?.pages?.length || 0}
                                  </TableCell>
                                </TableRow>
                                {pageStats?.pages && pageStats.pages.length > 0 && (
                                  <TableRow>
                                    <TableCell
                                      style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 48 }}
                                      colSpan={7}
                                    >
                                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box sx={{ margin: 2, marginLeft: '140px' }}>
                                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                                            Page Breakdown:
                                          </Typography>
                                          <Table size="small" sx={nestedTableStyles.container}>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={nestedTableStyles.headerCell}>Page</TableCell>
                                                <TableCell sx={nestedTableStyles.headerCell}>Page Owner</TableCell>
                                                <TableCell sx={nestedTableStyles.headerCell}>Offer ID + Name</TableCell>
                                                <TableCell sx={nestedTableStyles.headerCell}>Offer Element ID + Name</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>Views</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>Clicks</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>CTR</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>Sales</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>Revenue</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>CVR</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.headerCell}>RPV</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {pageStats.pages.map((page) => {
                                                const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                                                return (
                                                  <TableRow
                                                    key={isSnake ? 'snake' : page.pageNumber}
                                                    sx={isSnake ? nestedTableStyles.snakeRow : {}}
                                                  >
                                                    <TableCell component="th" scope="row" sx={nestedTableStyles.bodyCell}>
                                                      {isSnake ? 'Snake' : `Page ${page.pageNumber}`}
                                                    </TableCell>
                                                    <TableCell sx={nestedTableStyles.bodyCell}>
                                                      {page.pageOwnerUsername || page.pageOwnerName ? (
                                                        <Box>
                                                          <Typography variant="body2">
                                                            {page.pageOwnerName || 'N/A'}
                                                          </Typography>
                                                          {page.pageOwnerUsername && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                              @{page.pageOwnerUsername}
                                                            </Typography>
                                                          )}
                                                        </Box>
                                                      ) : (
                                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                                      )}
                                                    </TableCell>
                                                    <TableCell sx={nestedTableStyles.bodyCell}>
                                                      {page.offerId ? (
                                                        <Box>
                                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                            ID: {page.offerId}
                                                          </Typography>
                                                          <Typography variant="body2">
                                                            {page.offerName || 'Unknown Offer'}
                                                          </Typography>
                                                        </Box>
                                                      ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                          No offer
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                    <TableCell sx={nestedTableStyles.bodyCell}>
                                                      {page.offerElementId || page.offerElementName ? (
                                                        <Box>
                                                          {page.offerElementId && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                              ID: {page.offerElementId}
                                                            </Typography>
                                                          )}
                                                          <Typography variant="body2">
                                                            {page.offerElementName || 'Unknown Element'}
                                                          </Typography>
                                                        </Box>
                                                      ) : (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                          No element
                                                        </Typography>
                                                      )}
                                                    </TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.views.toLocaleString()}</TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.clicks?.toLocaleString() || 0}</TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.ctr || '0.00'}%</TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.sales || 0}</TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>
                                                      {page.cvr !== undefined ? `${page.cvr}%` : (page.clicks > 0 ? `${((page.sales || 0) / page.clicks * 100).toFixed(2)}%` : '0.00%')}
                                                    </TableCell>
                                                    <TableCell align="center" sx={nestedTableStyles.bodyCell}>
                                                      ${page.views > 0
                                                        ? ((page.totalRevenue || 0) / page.views).toFixed(2)
                                                        : '0.00'}
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })()}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No blog performance data available. Check the browser console for details.
        </Typography>
      )}
    </Box>
  );

  const renderSnakeStats = () => (
    <Box>
      {/* Snake Stats Summary Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Snake Stats
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2, py: 0.5, backgroundColor: 'warning.main', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>Total Snake:</Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Views: <strong>{(blogStats?.snake?.totals?.views || 0).toLocaleString()}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Sales: <strong>{blogStats?.snake?.totals?.sales || 0}</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Revenue: <strong>${(blogStats?.snake?.totals?.revenue || 0).toFixed(2)}</strong>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Snake stats show the performance of content from other affiliates that appears in merged blogs.
          </Typography>
        </CardContent>
      </Card>

      {blogStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : blogStatsError ? (
        <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
      ) : blogStats?.snake ? (
        <>
          {/* Overall Snake Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <VisibilityIcon color="warning" />
                    <Box>
                      <Typography variant="h4">{(blogStats.snake?.totals?.views || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Snake Views</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon color="success" />
                    <Box>
                      <Typography variant="h4">{(blogStats.snake?.totals?.sales || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Snake Sales</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachMoneyIcon color="warning" />
                    <Box>
                      <Typography variant="h4">${(blogStats.snake?.totals?.revenue || 0).toFixed(2)}</Typography>
                      <Typography variant="body2" color="text.secondary">Snake Revenue</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AssessmentIcon color="info" />
                    <Box>
                      <Typography variant="h4">
                        ${blogStats.snake?.totals?.views > 0
                          ? ((blogStats.snake?.totals?.revenue || 0) / blogStats.snake.totals.views).toFixed(2)
                          : '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">RPV (Revenue per View)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Snake Stats Table - Grouped by Blog with Expandable Details */}
          {blogStats.snake?.detailedPageStats && blogStats.snake.detailedPageStats.length > 0 && (() => {
            // Group snake pages by blog
            const snakeBlogGroups = {};
            blogStats.snake.detailedPageStats.forEach(page => {
              const key = page.blogPostId;
              if (!snakeBlogGroups[key]) {
                snakeBlogGroups[key] = {
                  blogPostId: page.blogPostId,
                  blogTitle: page.blogTitle,
                  affiliateId: page.affiliateId,
                  affiliateName: page.affiliateName,
                  affiliateUsername: page.affiliateUsername,
                  pages: [],
                  totals: { views: 0, clicks: 0, sales: 0, revenue: 0 }
                };
              }
              snakeBlogGroups[key].pages.push(page);
              snakeBlogGroups[key].totals.views += page.views || 0;
              snakeBlogGroups[key].totals.clicks += page.clicks || 0;
              snakeBlogGroups[key].totals.sales += page.sales || 0;
              snakeBlogGroups[key].totals.revenue += page.totalRevenue || 0;
            });

            const groupedBlogs = Object.values(snakeBlogGroups);

            return (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Snake Page Details by Blog
                  </Typography>
                  <TableContainer component={Paper} sx={tableStyles.container}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={tableStyles.headerRow}>
                          <TableCell padding="checkbox" sx={{ ...tableStyles.headerCell, width: 40 }} />
                          <TableCell sx={{ ...tableStyles.headerCell, width: 180 }}>Affiliate</TableCell>
                          <TableCell sx={{ ...tableStyles.headerCell, width: 200 }}>Blog</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Views</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Clicks</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>CTR</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Sales</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Revenue</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>RPV</TableCell>
                          <TableCell align="center" sx={tableStyles.headerCell}>Pages</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedBlogs.map((blogGroup) => {
                          const isExpanded = expandedSnakeBlogs[blogGroup.blogPostId];
                          const ctr = blogGroup.totals.views > 0
                            ? ((blogGroup.totals.clicks / blogGroup.totals.views) * 100).toFixed(2)
                            : '0.00';
                          const rpv = blogGroup.totals.views > 0
                            ? (blogGroup.totals.revenue / blogGroup.totals.views).toFixed(2)
                            : '0.00';

                          return (
                            <React.Fragment key={blogGroup.blogPostId}>
                              {/* Blog Summary Row */}
                              <TableRow
                                sx={{
                                  ...tableStyles.bodyRow,
                                  cursor: 'pointer',
                                }}
                                onClick={() => setExpandedSnakeBlogs(prev => ({
                                  ...prev,
                                  [blogGroup.blogPostId]: !prev[blogGroup.blogPostId]
                                }))}
                              >
                                <TableCell padding="checkbox" sx={tableStyles.bodyCell}>
                                  <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={tableStyles.bodyCell}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {blogGroup.affiliateName || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      @{blogGroup.affiliateUsername || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={tableStyles.bodyCell}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {blogGroup.blogTitle || `Blog ${blogGroup.blogPostId}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ID: {blogGroup.blogPostId}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">{blogGroup.totals.views.toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">{blogGroup.totals.clicks.toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">{ctr}%</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">{blogGroup.totals.sales}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">${blogGroup.totals.revenue.toFixed(2)}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">${rpv}</Typography>
                                </TableCell>
                                <TableCell align="center" sx={tableStyles.bodyCell}>
                                  <Typography fontWeight="bold">{blogGroup.pages.length}</Typography>
                                </TableCell>
                              </TableRow>

                              {/* Expanded Page Details */}
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={10} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 2, marginLeft: '320px' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                                          Page Breakdown:
                                        </Typography>
                                        <Table size="small" sx={nestedTableStyles.container}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={nestedTableStyles.headerCell}>Page</TableCell>
                                              <TableCell sx={nestedTableStyles.headerCell}>Page Owner</TableCell>
                                              <TableCell sx={nestedTableStyles.headerCell}>Offer</TableCell>
                                              <TableCell sx={nestedTableStyles.headerCell}>Element</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>Views</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>Clicks</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>CTR</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>Sales</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>CVR</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>Revenue</TableCell>
                                              <TableCell align="center" sx={nestedTableStyles.headerCell}>RPV</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {blogGroup.pages.map((page, pageIndex) => (
                                              <TableRow key={pageIndex}>
                                                <TableCell sx={nestedTableStyles.bodyCell}>Page {page.pageNumber}</TableCell>
                                                <TableCell sx={nestedTableStyles.bodyCell}>
                                                  {page.pageOwnerName || page.pageOwnerUsername ? (
                                                    <Box>
                                                      <Typography variant="body2">
                                                        {page.pageOwnerName || 'N/A'}
                                                      </Typography>
                                                      {page.pageOwnerUsername && (
                                                        <Typography variant="caption" color="text.secondary">
                                                          @{page.pageOwnerUsername}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  ) : '-'}
                                                </TableCell>
                                                <TableCell sx={nestedTableStyles.bodyCell}>
                                                  {page.offerId ? (
                                                    <Box>
                                                      <Typography variant="body2">
                                                        {page.offerName || 'Unknown'}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        ID: {page.offerId}
                                                      </Typography>
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                      No offer
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell sx={nestedTableStyles.bodyCell}>
                                                  {page.offerElementId ? (
                                                    <Box>
                                                      <Typography variant="body2">
                                                        {page.offerElementName || 'Unknown'}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        ID: {page.offerElementId}
                                                      </Typography>
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                      No element
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>{(page.views || 0).toLocaleString()}</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>{(page.clicks || 0).toLocaleString()}</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.ctr || '0.00'}%</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.sales || 0}</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>{page.cvr || '0.00'}%</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>${(page.totalRevenue || 0).toFixed(2)}</TableCell>
                                                <TableCell align="center" sx={nestedTableStyles.bodyCell}>${page.rpv || '0.00'}</TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })()}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No snake stats available. Snake stats appear when blogs have merged content from other affiliates.
        </Typography>
      )}
    </Box>
  );

  const renderCategoryStats = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CategoryIcon color="primary" />
                <Box>
                  <Typography variant="h4">28</Typography>
                  <Typography variant="body2" color="text.secondary">Total Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="success" />
                <Box>
                  <Typography variant="h4">24</Typography>
                  <Typography variant="body2" color="text.secondary">Active Categories</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="info" />
                <Box>
                  <Typography variant="h4">156</Typography>
                  <Typography variant="body2" color="text.secondary">Total Offers</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box>
                  <Typography variant="h4">5.6</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Offers/Category</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MockChart title="Category Performance" type="bar" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MockPieChart title="Category Distribution" data={categoryData} />
        </Grid>
      </Grid>
    </Box>
  );

  const tabContent = [
    renderOfferStats,
    renderAffiliateStats,
    renderBlogStats,
    renderSnakeStats,
    renderCategoryStats
  ];

  return (
    <DashboardLayout title="Reporting">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analytics and reporting dashboard
          </Typography>
        </Box>

        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Offer Stats" />
            <Tab label="Affiliate Stats" />
            <Tab label="Blog Stats" />
            <Tab label="Snake Stats" />
            <Tab label="Category Stats" />
          </Tabs>
        </Paper>

        <Box sx={{ py: 3 }}>
          {tabContent[activeTab]()}
        </Box>
      </Container>
    </DashboardLayout>
  );
};

export default Reporting; 